// Record identity + de-duplication for CV content.
//
// A CV document can accumulate the same underlying record more than once:
// an imported experience next to its AI-enhanced rewrite, a certification
// listed both as "PMP" and "PMP - Project Management Institute", a degree
// present as a structured entry and again from a second parse pass. The
// renderers print whatever they are given, so duplicates must be collapsed
// at the data layer. This module is the shared identity/dedupe vocabulary
// used by the export normalizer, the tailored-CV resolver, and the import
// mappers.
//
// Matching is deliberately conservative: a false merge permanently drops a
// real record once snapshots are backfilled, while a missed duplicate only
// keeps today's (wrong but recoverable) rendering. Every rule below requires
// a positive identity signal - never merge on absence of evidence.
//
// This module is intentionally dependency-free at runtime (only erased
// `import type`s): it is imported by the API, the mobile app, AND the
// node:test runner, so it avoids relative runtime imports whose extensionless
// resolution the test runner handles inconsistently (same policy as
// parsed-cv-to-overrides).

import type {
  SmartCVPublicCertification,
  SmartCVPublicEducation,
  SmartCVPublicExperience,
} from "@tabbio/types";

// ─── Normalization primitives ───────────────────────────────────

/** Lowercased, punctuation-free, single-spaced key for fuzzy comparison. */
export function normalizeCvMatchKey(value?: string | null): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Legal-form words that one parse keeps and another drops: "Google" vs
// "Google LLC" is one employer, not two. Mirrors the parser's suffix list.
const COMPANY_SUFFIX_WORDS = new Set([
  "inc",
  "incorporated",
  "llc",
  "llp",
  "lp",
  "ltd",
  "limited",
  "corp",
  "corporation",
  "co",
  "company",
  "plc",
  "gmbh",
  "ag",
  "bv",
  "nv",
  "sa",
  "sas",
  "srl",
  "spa",
  "pte",
  "pvt",
  "private",
  "group",
  "holding",
  "holdings",
  "fz",
  "fze",
  "fzco",
  "fzllc",
  "wll",
  "sal",
]);

/**
 * Employer identity key: normalized with trailing legal-form words stripped.
 * Never strips the whole name, so a company literally called "Group" keeps
 * a key.
 */
export function cvCompanyMatchKey(value?: string | null): string {
  const words = normalizeCvMatchKey(value).split(" ").filter(Boolean);
  while (words.length > 1 && COMPANY_SUFFIX_WORDS.has(words[words.length - 1])) {
    words.pop();
  }
  return words.join(" ");
}

/** ` a ` word-boundary containment: "amana healthcare" ⊂ "amana healthcare abu dhabi". */
function containsWholeWords(longer: string, shorter: string): boolean {
  return ` ${longer} `.includes(` ${shorter} `);
}

function wordCount(key: string): number {
  return key ? key.split(" ").length : 0;
}

const MONTH_NAMES: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * Loose date → "YYYY-MM" / "YYYY" / "" comparison key. Accepts ISO strings,
 * "2023-01", "Jan 2023", "01/2023" - the shapes that reach resolved CV
 * documents from imports, pickers and Prisma serialization.
 */
export function cvDateMatchKey(value?: string | null): string {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw || /^(present|current|now|ongoing)$/i.test(raw)) return "";
  const iso = raw.match(/^(\d{4})(?:-(\d{1,2}))?(?:-\d{1,2})?(?:[T ].*)?$/);
  if (iso) {
    return iso[2] ? `${iso[1]}-${String(Number(iso[2])).padStart(2, "0")}` : iso[1];
  }
  const named = raw.match(/^([A-Za-z]{3,9})\.?,?\s+(\d{4})$/);
  if (named) {
    const month = MONTH_NAMES[named[1].slice(0, 3).toLowerCase()];
    return month ? `${named[2]}-${String(month).padStart(2, "0")}` : named[2];
  }
  const numeric = raw.match(/^(\d{1,2})[/-](\d{4})$/);
  if (numeric) {
    return `${numeric[2]}-${String(Number(numeric[1])).padStart(2, "0")}`;
  }
  const yearOnly = raw.match(/\b(\d{4})\b/);
  return yearOnly ? yearOnly[1] : "";
}

/**
 * Two date keys are compatible when either is unknown, they are equal, or one
 * is the other's year ("2023" vs "2023-01"). Incompatible dates are the one
 * signal that keeps a promotion or re-hire at the same employer distinct.
 */
function dateKeysCompatible(a: string, b: string): boolean {
  if (!a || !b || a === b) return true;
  return a.slice(0, 4) === b.slice(0, 4) && (a.length === 4 || b.length === 4);
}

// ─── Experience identity ────────────────────────────────────────

/**
 * Structural superset of every experience shape in the system: resolved
 * documents carry `overview`/`bullets`, parsed imports carry
 * `description`/`achievements`. Identity only reads title/company/dates, so
 * one function serves both.
 */
export interface ExperienceRecordLike {
  id?: string;
  title?: string | null;
  company?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  current?: boolean | null;
  overview?: string | null;
  bullets?: string[] | null;
  description?: string | null;
  achievements?: string[] | null;
}

type ExperienceLike = ExperienceRecordLike;

/**
 * Two experience entries describe the same underlying role when they share a
 * real employer, their titles read as the same role (equal or one contained
 * in the other: "Chemistry Teacher" vs "Chemistry Teacher, Grade 11 & 12"),
 * and their dates do not contradict. A promotion track ("Engineer" →
 * "Senior Engineer") stays distinct because its start dates differ.
 */
export function isSameExperienceRecord(
  a: ExperienceLike,
  b: ExperienceLike,
): boolean {
  const companyA = cvCompanyMatchKey(a.company);
  const companyB = cvCompanyMatchKey(b.company);
  if (!companyA || !companyB) return false;
  const companiesMatch =
    companyA === companyB ||
    // One parse kept a trailing location ("Amana Healthcare, Abu Dhabi, UAE");
    // containment counts only when the shorter side is a real multi-word name,
    // so "Emirates" alone can never absorb every Emirates-prefixed employer.
    (containsWholeWords(companyA, companyB) && wordCount(companyB) >= 2) ||
    (containsWholeWords(companyB, companyA) && wordCount(companyA) >= 2);
  if (!companiesMatch) return false;

  const titleA = normalizeCvMatchKey(a.title);
  const titleB = normalizeCvMatchKey(b.title);
  const startA = cvDateMatchKey(a.startDate);
  const startB = cvDateMatchKey(b.startDate);
  const endA = cvDateMatchKey(a.endDate);
  const endB = cvDateMatchKey(b.endDate);

  if (titleA && titleB) {
    const titlesMatch =
      titleA === titleB ||
      containsWholeWords(titleA, titleB) ||
      containsWholeWords(titleB, titleA);
    if (!titlesMatch) return false;
  } else {
    // A title-less copy merges only when both dates pin it to the same role.
    if (!startA || !startB || startA !== startB) return false;
  }

  return dateKeysCompatible(startA, startB) && dateKeysCompatible(endA, endB);
}

function experienceContentWeight(entry: ExperienceLike): number {
  return (
    (entry.overview?.length ?? entry.description?.length ?? 0) +
    (entry.bullets ?? entry.achievements ?? []).reduce(
      (total, bullet) => total + bullet.length,
      0,
    )
  );
}

/**
 * Order-preserving collapse of duplicate experience entries. When two copies
 * share an identity the one carrying more content wins in the earlier
 * position, so a fuller entry is never lost to a sparser duplicate.
 */
export function dedupeExperienceRecords<T extends ExperienceLike>(
  entries: T[],
): T[] {
  const result: T[] = [];
  for (const entry of entries) {
    const existingIndex = result.findIndex((kept) =>
      isSameExperienceRecord(kept, entry),
    );
    if (existingIndex < 0) {
      result.push(entry);
      continue;
    }
    if (
      experienceContentWeight(entry) >
      experienceContentWeight(result[existingIndex])
    ) {
      result[existingIndex] = entry;
    }
  }
  return result;
}

// ─── Education identity ─────────────────────────────────────────

// Degree abbreviations both parses use interchangeably; expanded before
// comparison so "BSc Chemistry" and "Bachelor of Science, Chemistry" collapse.
const DEGREE_ALIASES: Array<[RegExp, string]> = [
  [/\bb\s?sc\b/g, "bachelor of science"],
  [/\bb\s?a\b/g, "bachelor of arts"],
  [/\bb\s?ed\b/g, "bachelor of education"],
  [/\bb\s?e\b/g, "bachelor of engineering"],
  [/\bb\s?tech\b/g, "bachelor of technology"],
  [/\bbba\b/g, "bachelor of business administration"],
  [/\bm\s?sc\b/g, "master of science"],
  [/\bm\s?a\b/g, "master of arts"],
  [/\bm\s?ed\b/g, "master of education"],
  [/\bm\s?tech\b/g, "master of technology"],
  [/\bmba\b/g, "master of business administration"],
  [/\bphd\b/g, "doctor of philosophy"],
];

function degreeMatchKey(
  degree?: string | null,
  fieldOfStudy?: string | null,
): string {
  let key = normalizeCvMatchKey(
    [degree ?? "", fieldOfStudy ?? ""].filter(Boolean).join(" "),
  );
  for (const [pattern, expansion] of DEGREE_ALIASES) {
    key = key.replace(pattern, expansion);
  }
  return key.replace(/\s+/g, " ").trim();
}

/** Structural superset of resolved and parsed education shapes. */
export interface EducationRecordLike {
  id?: string;
  school?: string | null;
  degree?: string | null;
  fieldOfStudy?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  grade?: string | null;
  description?: string | null;
  details?: string[] | null;
}

type EducationLike = EducationRecordLike;

/**
 * Two education entries are the same record when the schools match (equal or
 * containment: "University of Technology" vs "University of Technology,
 * Iraq"), the degree+field texts do not contradict, and the years agree.
 * Two different degrees at the same school ("BSc Chemistry" and "B.Ed") stay
 * distinct because neither degree text contains the other.
 */
export function isSameEducationRecord(
  a: EducationLike,
  b: EducationLike,
): boolean {
  const schoolA = normalizeCvMatchKey(a.school);
  const schoolB = normalizeCvMatchKey(b.school);
  // A copy that lost its school (a parse split: one row kept "MBA" alone, the
  // next kept "MBA — Abu Dhabi University") can still be the same record, but
  // only under the strictest remaining evidence: both degree texts present
  // and matching, and the years agreeing. Two school-less rows never match —
  // there is not enough identity to merge on.
  if (!schoolA && !schoolB) return false;
  if (!schoolA || !schoolB) {
    const degreeA = degreeMatchKey(a.degree, a.fieldOfStudy);
    const degreeB = degreeMatchKey(b.degree, b.fieldOfStudy);
    if (!degreeA || !degreeB || !degreeWordsMatch(degreeA, degreeB)) {
      return false;
    }
    const yearA = cvDateMatchKey(a.endDate) || cvDateMatchKey(a.startDate);
    const yearB = cvDateMatchKey(b.endDate) || cvDateMatchKey(b.startDate);
    return dateKeysCompatible(yearA.slice(0, 4), yearB.slice(0, 4));
  }
  const schoolsMatch =
    schoolA === schoolB ||
    containsWholeWords(schoolA, schoolB) ||
    containsWholeWords(schoolB, schoolA);
  if (!schoolsMatch) return false;

  const degreeA = degreeMatchKey(a.degree, a.fieldOfStudy);
  const degreeB = degreeMatchKey(b.degree, b.fieldOfStudy);
  if (degreeA && degreeB && !degreeWordsMatch(degreeA, degreeB)) {
    return false;
  }

  const yearA = cvDateMatchKey(a.endDate) || cvDateMatchKey(a.startDate);
  const yearB = cvDateMatchKey(b.endDate) || cvDateMatchKey(b.startDate);
  return dateKeysCompatible(yearA.slice(0, 4), yearB.slice(0, 4));
}

/**
 * Word-subset rather than phrase containment: "Bachelor, Electrical
 * Engineering" and "Bachelor of Engineering - Electrical Engineering" order
 * their words differently but describe one degree. A genuinely different
 * degree always carries a word the other lacks ("chemistry" vs "education").
 */
function degreeWordsMatch(degreeA: string, degreeB: string): boolean {
  const wordsA = new Set(degreeA.split(" "));
  const wordsB = new Set(degreeB.split(" "));
  const [smaller, larger] =
    wordsA.size <= wordsB.size ? [wordsA, wordsB] : [wordsB, wordsA];
  const FILLER = new Set(["of", "in", "and", "the"]);
  return [...smaller].every((word) => FILLER.has(word) || larger.has(word));
}

function educationContentWeight(entry: EducationLike): number {
  return (
    // School counts toward richness so that when a school-less parse split
    // matches its complete twin, the copy that kept the school wins.
    (entry.school?.length ?? 0) +
    (entry.degree?.length ?? 0) +
    (entry.fieldOfStudy?.length ?? 0) +
    (entry.description?.length ?? 0) +
    (entry.details ?? []).reduce((total, detail) => total + detail.length, 0)
  );
}

/** Order-preserving collapse of duplicate education entries; richer copy wins. */
export function dedupeEducationRecords<T extends EducationLike>(
  entries: T[],
): T[] {
  const result: T[] = [];
  for (const entry of entries) {
    const existingIndex = result.findIndex((kept) =>
      isSameEducationRecord(kept, entry),
    );
    if (existingIndex < 0) {
      result.push(entry);
      continue;
    }
    if (
      educationContentWeight(entry) >
      educationContentWeight(result[existingIndex])
    ) {
      result[existingIndex] = entry;
    }
  }
  return result;
}

// ─── Certification identity ─────────────────────────────────────

// Credential names and the acronyms CVs list them under. Replacing the full
// form with its acronym makes "Project Management Professional" and "PMP"
// produce the same key ("pmp pmp" collapses to "pmp" below).
const CERT_ALIASES: Array<[RegExp, string]> = [
  [/\bproject management professional\b/g, "pmp"],
  [/\bprofessional scrum product owner\b/g, "pspo"],
  [/\bprofessional scrum master\b/g, "psm"],
  [/\bcertified scrum master\b/g, "csm"],
  [/\bcertified associate in project management\b/g, "capm"],
  [/\bpmi agile certified practitioner\b/g, "pmi acp"],
  [/\binternational software testing qualifications board\b/g, "istqb"],
  [/\bcertified information systems security professional\b/g, "cissp"],
  [/\bcertified information systems auditor\b/g, "cisa"],
  [/\bcertified information security manager\b/g, "cism"],
  [/\bcertified public accountant\b/g, "cpa"],
  [/\bchartered financial analyst\b/g, "cfa"],
  [/\bchartered institute of procurement and supply\b/g, "cips"],
  [/\bcertified supply chain professional\b/g, "cscp"],
  [/\bcertified in production and inventory management\b/g, "cpim"],
  [/\bsociety for human resource management\b/g, "shrm"],
  [/\bamazon web services\b/g, "aws"],
];

/** Canonical comparison key for a credential name. */
export function cvCertificationMatchKey(name?: string | null): string {
  let key = normalizeCvMatchKey(name);
  for (const [pattern, acronym] of CERT_ALIASES) {
    key = key.replace(pattern, acronym);
  }
  // "pmp pmp" after alias replacement, or "PMP (PMP)" in the source.
  const words = key.split(" ").filter(Boolean);
  const deduped: string[] = [];
  for (const word of words) {
    if (deduped[deduped.length - 1] !== word) deduped.push(word);
  }
  return deduped.join(" ");
}

/** Structural superset of resolved and parsed certification shapes. */
export interface CertificationRecordLike {
  id?: string;
  name?: string | null;
  issuer?: string | null;
  issueDate?: string | null;
  expirationDate?: string | null;
  credentialId?: string | null;
  credentialUrl?: string | null;
}

type CertificationLike = CertificationRecordLike;

/**
 * Two certification entries are the same credential when their canonical
 * names match (equal or containment: "PMP" vs "PMP - Project Management
 * Institute - 2021") and their issuers do not contradict.
 */
export function isSameCertificationRecord(
  a: CertificationLike,
  b: CertificationLike,
): boolean {
  const nameA = cvCertificationMatchKey(a.name);
  const nameB = cvCertificationMatchKey(b.name);
  if (!nameA || !nameB) return false;
  const shorter = nameA.length <= nameB.length ? nameA : nameB;
  const namesMatch =
    nameA === nameB ||
    (shorter.length >= 3 &&
      (containsWholeWords(nameA, nameB) || containsWholeWords(nameB, nameA)));
  if (!namesMatch) return false;

  const issuerA = normalizeCvMatchKey(a.issuer);
  const issuerB = normalizeCvMatchKey(b.issuer);
  const comparableIssuerA = isAcronymIssuerEcho(a.issuer, nameA)
    ? ""
    : issuerA;
  const comparableIssuerB = isAcronymIssuerEcho(b.issuer, nameB)
    ? ""
    : issuerB;
  if (comparableIssuerA && comparableIssuerB) {
    const issuersCompatible =
      comparableIssuerA === comparableIssuerB ||
      containsWholeWords(comparableIssuerA, comparableIssuerB) ||
      containsWholeWords(comparableIssuerB, comparableIssuerA) ||
      // "PMI" vs "Project Management Institute": one side is the other's
      // initialism.
      comparableIssuerA.replace(/\s/g, "") ===
        initialismOf(comparableIssuerB) ||
      comparableIssuerB.replace(/\s/g, "") ===
        initialismOf(comparableIssuerA);
    if (issuersCompatible) return true;
    // Two copies with the EXACT same credential name where one "issuer" is
    // just a fragment of that name ("CSA Finance Course" issued by "CSA")
    // are one credential whose issuer field caught parse debris.
    if (nameA === nameB) {
      return (
        containsWholeWords(nameA, issuerA) || containsWholeWords(nameB, issuerB)
      );
    }
    return false;
  }
  return true;
}

/**
 * Drop "certifications" that are really the ISSUER line of a neighbouring
 * credential ("Great Learning", "Oxford Study Center", a bare "GE") - a
 * common re-parse artifact of name-over-issuer certificate layouts. Only a
 * dateless, credential-less entry whose name matches an issuer used in the
 * same list (or its own issuer) is dropped.
 */
export function dropCertificationIssuerEchoes<T extends CertificationLike>(
  certifications: T[],
): T[] {
  const issuerKeys = new Set(
    certifications
      .map((cert) => normalizeCvMatchKey(cert.issuer))
      .filter((key) => key.length >= 2),
  );
  // Whatever remains of the name once the issuer phrase is removed must be
  // pure line debris (a date, "issued", "certificate") for the entry to
  // count as an echo - "Harvard Business School Leadership Program" is a
  // real credential even though another entry's issuer is contained in it.
  // Date-line tokens only. "Certification"/"Certificate" are NOT debris:
  // "CIPD Certification" issued by CIPD is a real credential named after its
  // issuer, a common pattern.
  const DEBRIS_WORD =
    /^(issued?|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december|(19|20)\d{2})$/;
  const isIssuerEchoName = (nameKey: string) => {
    if (issuerKeys.has(nameKey)) return true;
    for (const issuerKey of issuerKeys) {
      if (!containsWholeWords(nameKey, issuerKey)) continue;
      const residue = ` ${nameKey} `
        .replace(` ${issuerKey} `, ' ')
        .split(' ')
        .filter(Boolean);
      if (residue.every((word) => DEBRIS_WORD.test(word))) return true;
    }
    return false;
  };

  return certifications.filter((cert) => {
    // A real credential id or verification URL vouches for the entry. An
    // issue DATE does not: the mis-parsed issuer line often absorbs its
    // neighbour's date ("Cambridge College of International Studies ·
    // Issued Jan 2026" next to the diploma that college issued).
    if (cert.credentialUrl || cert.credentialId) return true;
    const nameKey = normalizeCvMatchKey(cert.name);
    if (!nameKey || !isIssuerEchoName(nameKey)) return true;
    // An entry with its own DISTINCT issuer is a real credential that merely
    // shares a name; only issuer-less (or self-issued) echoes drop.
    const issuerKey = normalizeCvMatchKey(cert.issuer);
    return Boolean(issuerKey && !containsWholeWords(nameKey, issuerKey));
  });
}

function initialismOf(key: string): string {
  const words = key.split(" ").filter(Boolean);
  if (words.length < 2) return "";
  return words.map((word) => word[0]).join("");
}

/**
 * A deterministic parse can mistake a technical acronym already present in a
 * credential name ("SE", "REST", "SQL") for the issuer. Treat that narrow
 * shape as parse debris when comparing two otherwise matching credentials.
 * Real organization names and mixed-case issuers still have to agree.
 */
function isAcronymIssuerEcho(
  rawIssuer: string | null | undefined,
  nameKey: string,
): boolean {
  const issuer = (rawIssuer ?? "").trim();
  if (!/^[A-Z][A-Z0-9.+#/-]{1,11}$/.test(issuer)) return false;
  const issuerKey = normalizeCvMatchKey(issuer);
  return Boolean(issuerKey && containsWholeWords(nameKey, issuerKey));
}

function certificationContentWeight(entry: CertificationLike): number {
  const nameKey = normalizeCvMatchKey(entry.name);
  const issuerKey = normalizeCvMatchKey(entry.issuer);
  // An issuer that is a fragment of the name ("CSA" for "CSA Finance
  // Course") is parse debris and must not out-weigh a real issuer.
  const issuerWeight =
    issuerKey && !containsWholeWords(nameKey, issuerKey)
      ? (entry.issuer?.length ?? 0)
      : 0;
  return (
    (entry.name?.length ?? 0) +
    issuerWeight +
    (entry.issueDate ? 8 : 0) +
    (entry.credentialUrl ? 8 : 0)
  );
}

/** Order-preserving collapse of duplicate certifications; richer copy wins. */
export function dedupeCertificationRecords<T extends CertificationLike>(
  entries: T[],
): T[] {
  const result: T[] = [];
  for (const entry of entries) {
    const existingIndex = result.findIndex((kept) =>
      isSameCertificationRecord(kept, entry),
    );
    if (existingIndex < 0) {
      result.push(entry);
      continue;
    }
    if (
      certificationContentWeight(entry) >
      certificationContentWeight(result[existingIndex])
    ) {
      result[existingIndex] = entry;
    }
  }
  return result;
}

// ─── Highlights ─────────────────────────────────────────────────

export type HighlightLike = { title: string; desc: string } & Partial<{
  id: string;
}>;

function highlightMatchKey(entry: HighlightLike): string {
  return normalizeCvMatchKey(`${entry.title} ${entry.desc}`);
}

/** Collapse highlights that restate each other (equal or containment). */
export function dedupeHighlightRecords<T extends HighlightLike>(
  entries: T[],
): T[] {
  const result: T[] = [];
  for (const entry of entries) {
    const key = highlightMatchKey(entry);
    if (!key) continue;
    const existingIndex = result.findIndex((kept) => {
      const keptKey = highlightMatchKey(kept);
      return (
        keptKey === key ||
        (key.length >= 30 && containsWholeWords(keptKey, key)) ||
        (keptKey.length >= 30 && containsWholeWords(key, keptKey))
      );
    });
    if (existingIndex < 0) {
      result.push(entry);
      continue;
    }
    if (
      highlightMatchKey(entry).length >
      highlightMatchKey(result[existingIndex]).length
    ) {
      result[existingIndex] = entry;
    }
  }
  return result;
}

/**
 * A credential is not a career highlight: when a structured Certifications
 * section already lists it, a short highlight that merely names the same
 * credential is the redundant copy and is dropped.
 */
export function dropCertificationEchoHighlights<T extends HighlightLike>(
  highlights: T[],
  certifications: CertificationLike[],
): T[] {
  const certKeys = certifications
    .map((cert) => cvCertificationMatchKey(cert.name))
    .filter((key) => key.length >= 3);
  if (!certKeys.length) return highlights;
  return highlights.filter((highlight) => {
    const text = `${highlight.title} ${highlight.desc}`.trim();
    if (text.length > 120) return true;
    const key = cvCertificationMatchKey(text);
    return !certKeys.some(
      (certKey) => key === certKey || containsWholeWords(key, certKey),
    );
  });
}

// ─── Overview ↔ bullets echo suppression ────────────────────────

// Common function words carry no signal about whether two sentences say the
// same thing; excluded before comparing an overview to a bullet. Mirrors the
// parser's stopword list.
const COVERAGE_STOPWORDS = new Set([
  "the", "and", "for", "with", "that", "this", "from", "into", "their",
  "they", "them", "was", "were", "had", "has", "have", "been", "are", "our",
  "out", "across", "through", "using", "used", "via", "per", "role", "work",
  "worked", "including", "include", "which", "while", "also", "well", "both",
]);

/** Content words (≥3 chars, non-stopword) for overlap comparison. */
export function significantCvWords(value?: string | null): string[] {
  return normalizeCvMatchKey(value)
    .split(" ")
    .filter((word) => word.length >= 3 && !COVERAGE_STOPWORDS.has(word));
}

/**
 * True when a prose block is just these bullets strung together - the shape
 * an imported bulleted CV takes once its bullets collapse onto one line.
 * Such an overview adds nothing the bullets do not already say.
 */
export function isTextCoveredByBullets(
  text: string | null | undefined,
  bullets: string[],
): boolean {
  const words = significantCvWords(text);
  if (words.length < 4 || !bullets.length) return false;
  const bulletWords = new Set(
    bullets.flatMap((bullet) => significantCvWords(bullet)),
  );
  const covered = words.filter((word) => bulletWords.has(word)).length;
  return covered / words.length >= 0.7;
}

/**
 * Drop any bullet whose content words are almost entirely already present in
 * the overview. Never empties the list: when every bullet would drop, the
 * bullets (which carry the specific, quantified detail) are kept and the
 * overview is the acceptable echo.
 */
export function dropBulletsCoveredByText(
  text: string | null | undefined,
  bullets: string[],
): string[] {
  const textWords = new Set(significantCvWords(text));
  if (textWords.size < 4) return bullets;
  const kept = bullets.filter((bullet) => {
    const words = significantCvWords(bullet);
    if (words.length < 4) return true;
    const covered = words.filter((word) => textWords.has(word)).length;
    return covered / words.length < 0.7;
  });
  return kept.length ? kept : bullets;
}

// ─── Skill group budget ─────────────────────────────────────────

/** Budget for a generated CV's Skills section (the two-page CV target). */
export const CV_SKILL_GROUPS_MAX = 4;
export const CV_SKILLS_TOTAL_MAX = 15;

/**
 * Clamp generated skill groups to the section budget: at most
 * `CV_SKILL_GROUPS_MAX` groups and `CV_SKILLS_TOTAL_MAX` skills in total
 * (overridable per caller - the skills catalog specifies its own maxima).
 * Order-preserving - earlier groups and items win; case-insensitive de-dup
 * across groups; emptied groups drop. Prompt length rules are advisory to
 * the model; this clamp is the guarantee.
 */
export function clampCvSkillGroups(
  groups: Array<{ category: string; items: string[] }>,
  limits?: { maxGroups?: number; maxTotal?: number },
): Array<{ category: string; items: string[] }> {
  const maxGroups = limits?.maxGroups ?? CV_SKILL_GROUPS_MAX;
  const maxTotal = limits?.maxTotal ?? CV_SKILLS_TOTAL_MAX;
  const seen = new Set<string>();
  let remaining = maxTotal;
  const clamped: Array<{ category: string; items: string[] }> = [];
  for (const group of groups) {
    if (clamped.length >= maxGroups || remaining <= 0) break;
    const items: string[] = [];
    for (const item of group.items) {
      if (remaining <= 0) break;
      const trimmed = item.trim();
      const key = trimmed.toLowerCase();
      if (!trimmed || seen.has(key)) continue;
      seen.add(key);
      items.push(trimmed);
      remaining -= 1;
    }
    if (items.length > 0) {
      clamped.push({ category: group.category, items });
    }
  }
  return clamped;
}

// ─── Document-level cleanup ─────────────────────────────────────

export interface CvDedupeSections {
  experiences: SmartCVPublicExperience[];
  educations: SmartCVPublicEducation[];
  certifications: SmartCVPublicCertification[];
  highlights: HighlightLike[];
}

export interface CvDedupeResult<T extends CvDedupeSections> {
  cleaned: T;
  /** Entries removed per section - 0 everywhere means the doc was untouched. */
  removed: {
    experiences: number;
    educations: number;
    certifications: number;
    highlights: number;
  };
}

/**
 * Apply record de-duplication + overview/bullets echo suppression to a
 * resolved CV document's sections. Pure and order-preserving; returns the
 * same object when nothing changed so callers can cheaply detect no-ops.
 */
export function dedupeCvDocumentSections<T extends CvDedupeSections>(
  doc: T,
): CvDedupeResult<T> {
  const experiences = dedupeExperienceRecords(doc.experiences ?? []).map(
    (experience) => {
      // An imported description that is just the bullets strung together
      // renders as the same content twice inside one entry.
      if (
        experience.overview &&
        (experience.bullets ?? []).length > 0 &&
        isTextCoveredByBullets(experience.overview, experience.bullets)
      ) {
        return { ...experience, overview: null };
      }
      return experience;
    },
  );
  const educations = dedupeEducationRecords(doc.educations ?? []);
  const certifications = dropCertificationIssuerEchoes(
    dedupeCertificationRecords(doc.certifications ?? []),
  );
  const highlights = dropCertificationEchoHighlights(
    dedupeHighlightRecords(doc.highlights ?? []),
    certifications,
  );

  const removed = {
    experiences: (doc.experiences?.length ?? 0) - experiences.length,
    educations: (doc.educations?.length ?? 0) - educations.length,
    certifications: (doc.certifications?.length ?? 0) - certifications.length,
    highlights: (doc.highlights?.length ?? 0) - highlights.length,
  };
  const overviewsSuppressed = experiences.some(
    (experience, index) => experience !== (doc.experiences ?? [])[index],
  );
  if (
    !removed.experiences &&
    !removed.educations &&
    !removed.certifications &&
    !removed.highlights &&
    !overviewsSuppressed
  ) {
    return { cleaned: doc, removed };
  }

  return {
    // The rebuilt sections keep their element types; the cast re-attaches the
    // caller's wider document type around them.
    cleaned: {
      ...doc,
      experiences,
      educations,
      certifications,
      highlights,
    } as T,
    removed,
  };
}
